// ============================================================================
// ENTRY ANIMATIONS - Elements that animate in when the spinner first loads
// ============================================================================

// Main container animation - fades in and slides up from below
export const CONTAINER_ANIMATION = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' },
};

// Empty spacing div animation - fades in and slides down from above
export const SPACING_ANIMATION = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay: 0.2 },
};

// Subtitle animation - fades in and slides down from above with delay
export const SUBTITLE_ANIMATION = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay: 0.4 },
};

// ============================================================================
// INTERACTIVE ELEMENT ANIMATIONS - Button and wheel interactions
// ============================================================================

// Wheel button animations - handles hover, tap, and entry animations
export const WHEEL_BUTTON_ANIMATIONS = {
  whileHover: { scale: 1.05 }, // Grows 5% on hover
  whileTap: { scale: 0.95 }, // Shrinks 5% when clicked
  initial: { scale: 0.8, opacity: 0 }, // Starts small and invisible
  animate: { scale: 1, opacity: 1 }, // Animates to full size and visible
  transition: {
    duration: 0.8,
    delay: 0.6, // Delays after subtitle animation
    type: 'spring', // Bouncy spring animation
    stiffness: 100, // How bouncy the spring is
  },
};

// ============================================================================
// WHEEL ANIMATIONS - Core wheel spinning and segment animations
// ============================================================================

// Wheel rotation animation - handles the main spinning motion
export const WHEEL_ROTATION_ANIMATION = {
  animate: (rotation: number) => ({ rotate: rotation }), // Rotates to the calculated final position
  transition: (isAnimating: boolean) => ({
    duration: isAnimating ? 3 : 0, // 3 seconds when spinning, instant when not
    ease: isAnimating ? 'easeOut' : 'linear', // Slows down at end when spinning
    type: 'spring', // Spring physics for natural movement
    stiffness: isAnimating ? 50 : 100, // Less stiff when spinning for smoother motion
  }),
  whileHover: (isAnimating: boolean) => (!isAnimating ? { scale: 1.02 } : {}), // Slight grow on hover when not spinning
};

// Individual wheel segments animation - staggered entrance effect
export const SEGMENT_ANIMATION = {
  initial: { opacity: 0, scale: 0 }, // Start invisible and tiny
  animate: { opacity: 1, scale: 1 }, // Fade in and scale to full size
  transition: (index: number) => ({
    duration: 0.5,
    delay: index * 0.1, // Each segment starts 0.1s after the previous
    type: 'spring', // Bouncy spring animation
    stiffness: 200, // Very bouncy for segments
  }),
};

// ============================================================================
// FEEDBACK ANIMATIONS - Visual feedback during spinning
// ============================================================================

// Pointer arrow animation - wiggles and grows during spin for visual feedback
export const POINTER_ANIMATION = {
  animate: (isAnimating: boolean) =>
    isAnimating
      ? {
          rotate: [0, 5, -5, 0], // Wiggle left and right
          scale: [1, 1.1, 1], // Grow and shrink
        }
      : {},
  transition: {
    duration: 0.5,
    repeat: (isAnimating: boolean) => (isAnimating ? Infinity : 0), // Repeat infinitely while spinning
    ease: 'easeInOut',
  },
};
